import { useEffect, useState, useCallback } from "react";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Plane, Hotel, CalendarDays } from "lucide-react";

const CATS = { umrah: "Umrah", hajj: "Hajj", muslim_tour: "Muslim Tour", domestic: "Domestic" };
const CAT_STYLE = {
  umrah: "bg-emerald-50 text-emerald-700 border-emerald-200",
  hajj: "bg-gold-100 text-gold-700 border-gold-500/30",
  muslim_tour: "bg-sky-50 text-sky-700 border-sky-200",
};

export default function Packages() {
  const { t } = useLang();
  const [packages, setPackages] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "umrah", destination: "Makkah & Madinah", duration: "12D10N", airline: "", hotel_makkah: "", hotel_madinah: "", price: 8000 });

  const load = useCallback(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => toast.error("Failed to load"));
  }, []);
  useEffect(load, [load]);

  const createPkg = async (e) => {
    e.preventDefault();
    try {
      await api.post("/packages", { ...form, price: Number(form.price) });
      toast.success("Package created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (!packages) return <div className="flex justify-center h-64 items-center" data-testid="packages-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="packages-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.packages")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{packages.length} active packages</p>
        </div>
        <Button data-testid="add-package-button" onClick={() => setOpen(true)} className="bg-brand-800 hover:bg-brand-700 text-sand rounded-full">
          <Plus className="w-4 h-4 mr-1.5" />{t("common.add")}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {packages.map((p) => (
          <div key={p.package_id} data-testid={`package-card-${p.package_code}`}
            className="pro-card overflow-hidden">
            <div className="bg-brand-950 p-5 relative">
              <Badge className={`border ${CAT_STYLE[p.category] || CAT_STYLE.umrah} absolute top-4 right-4`}>{CATS[p.category] || p.category}</Badge>
              <div className="text-[10px] font-mono text-gold-500 uppercase tracking-[0.2em]">{p.package_code}</div>
              <h3 className="font-serif text-xl text-sand mt-1.5 pr-16 leading-snug">{p.name}</h3>
              <div className="text-sand/50 text-xs mt-1">{p.destination} • {p.duration}</div>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground"><Plane className="w-3.5 h-3.5 text-brand-700" />{p.airline || "—"}</div>
                <div className="flex items-center gap-1.5 text-muted-foreground"><Hotel className="w-3.5 h-3.5 text-brand-700" />{p.room_type}</div>
                <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground truncate"><Hotel className="w-3.5 h-3.5 text-gold-600 shrink-0" />{p.hotel_makkah || "—"}</div>
                <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground truncate"><Hotel className="w-3.5 h-3.5 text-brand-500 shrink-0" />{p.hotel_madinah || "—"}</div>
              </div>
              <div className="flex items-end justify-between pt-3 border-t border-sand-border">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">from</div>
                  <div className="font-sans text-2xl font-bold text-brand-950 tabular-nums">{fmtRM(p.price)}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground"><CalendarDays className="w-3.5 h-3.5" />{p.departures.length} departures</div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                    {p.departures.slice(0, 3).map((d) => fmtDate(d.date)).join(" • ")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-package-dialog" className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">{t("common.add")} Package</DialogTitle></DialogHeader>
          <form onSubmit={createPkg} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>{t("common.name")}</Label>
                <Input data-testid="package-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="package-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration</Label>
                <Input data-testid="package-duration-input" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} />
              </div>
              <div>
                <Label>Airline</Label>
                <Input data-testid="package-airline-input" value={form.airline} onChange={(e) => setForm({ ...form, airline: e.target.value })} />
              </div>
              <div>
                <Label>Price (RM)</Label>
                <Input data-testid="package-price-input" type="number" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <Label>Hotel Makkah</Label>
                <Input data-testid="package-hotel-makkah-input" value={form.hotel_makkah} onChange={(e) => setForm({ ...form, hotel_makkah: e.target.value })} />
              </div>
              <div>
                <Label>Hotel Madinah</Label>
                <Input data-testid="package-hotel-madinah-input" value={form.hotel_madinah} onChange={(e) => setForm({ ...form, hotel_madinah: e.target.value })} />
              </div>
            </div>
            <Button data-testid="package-submit-button" type="submit" className="w-full bg-brand-800 hover:bg-brand-700 text-sand rounded-full">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
